import { useState } from 'react';
import { useModelInfo, useTrainModel, useModelVersions } from '@/hooks';
import type { TrainingResponse, FeatureImportance, ConfusionMatrixData } from '@/api/risk';

// Metric card component
function MetricCard({
  label,
  value,
  subValue,
  highlight = false
}: {
  label: string;
  value: string | number;
  subValue?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`border p-4 ${highlight ? 'border-mono-400 bg-mono-900' : 'border-mono-700'}`}>
      <p className="text-[10px] text-mono-500 tracking-widest">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${highlight ? 'text-mono-100' : 'text-mono-200'}`}>
        {value}
      </p>
      {subValue && (
        <p className="text-[10px] text-mono-500 mt-1">{subValue}</p>
      )}
    </div>
  );
}

// Feature importance bar chart
function FeatureImportanceChart({
  data,
  title
}: {
  data: FeatureImportance[];
  title: string;
}) {
  const maxImportance = Math.max(...data.map(d => d.importance));

  return (
    <div className="border border-mono-800">
      <div className="border-b border-mono-800 px-4 py-3">
        <h4 className="text-xs text-mono-300 tracking-widest">{title}</h4>
      </div>
      <div className="p-4 space-y-2">
        {data.slice(0, 10).map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-32 text-[10px] text-mono-400 truncate" title={item.feature}>
              {item.feature.replace(/_/g, ' ').toUpperCase()}
            </div>
            <div className="flex-1 h-4 bg-mono-900 relative">
              <div
                className="h-full bg-mono-500 transition-all duration-500"
                style={{ width: `${(item.importance / maxImportance) * 100}%` }}
              />
              <span className="absolute right-2 top-0 text-[10px] text-mono-300 leading-4">
                {(item.importance * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Confusion matrix visualization
function ConfusionMatrix({
  data,
  title
}: {
  data: ConfusionMatrixData;
  title: string;
}) {
  const { matrix, labels } = data;
  const maxVal = Math.max(...matrix.flat());

  return (
    <div className="border border-mono-800">
      <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
        <h4 className="text-xs text-mono-300 tracking-widest">{title}</h4>
        {data.precision !== undefined && (
          <div className="flex gap-4 text-[10px] text-mono-500">
            <span>PREC: {(data.precision * 100).toFixed(1)}%</span>
            <span>REC: {(data.recall! * 100).toFixed(1)}%</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex">
          {/* Y-axis label */}
          <div className="flex flex-col justify-center pr-2">
            <span className="text-[10px] text-mono-500 tracking-widest -rotate-90 origin-center whitespace-nowrap">
              ACTUAL
            </span>
          </div>

          <div className="flex-1">
            {/* X-axis header */}
            <div className="flex mb-2">
              <div className="w-12" />
              {labels.map((label, i) => (
                <div key={i} className="flex-1 text-center text-[10px] text-mono-500">
                  {label}
                </div>
              ))}
            </div>

            {/* Matrix rows */}
            {matrix.map((row, rowIdx) => (
              <div key={rowIdx} className="flex">
                <div className="w-12 flex items-center text-[10px] text-mono-500">
                  {labels[rowIdx]}
                </div>
                {row.map((val, colIdx) => {
                  const intensity = maxVal > 0 ? val / maxVal : 0;
                  const isCorrect = rowIdx === colIdx;
                  return (
                    <div
                      key={colIdx}
                      className={`flex-1 aspect-square m-0.5 flex items-center justify-center text-xs font-mono border ${
                        isCorrect
                          ? 'border-mono-500'
                          : 'border-mono-800'
                      }`}
                      style={{
                        backgroundColor: isCorrect
                          ? `rgba(200, 200, 200, ${intensity * 0.4})`
                          : `rgba(100, 100, 100, ${intensity * 0.3})`
                      }}
                    >
                      <span className={val > 0 ? 'text-mono-200' : 'text-mono-600'}>
                        {val}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* X-axis label */}
            <div className="text-center mt-2 text-[10px] text-mono-500 tracking-widest">
              PREDICTED
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Training results panel
function TrainingResults({ results }: { results: TrainingResponse }) {
  const [activeTab, setActiveTab] = useState<'metrics' | 'features' | 'confusion'>('metrics');

  const tabs = [
    { id: 'metrics' as const, label: 'METRICS' },
    { id: 'features' as const, label: 'FEATURES' },
    { id: 'confusion' as const, label: 'CONFUSION' },
  ];

  return (
    <div className="space-y-4">
      {/* Success header */}
      <div className="border border-mono-600 bg-mono-900 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-mono-300 tracking-widest">TRAINING COMPLETE</p>
            <p className="text-sm text-mono-100 mt-1">{results.message}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-mono-500">VERSION</p>
            <p className="text-sm text-mono-200 font-mono">{results.model_version}</p>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-mono-800 flex">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-xs tracking-widest transition-colors ${
              activeTab === tab.id
                ? 'text-mono-100 border-b border-mono-100 -mb-px'
                : 'text-mono-500 hover:text-mono-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'metrics' && (
        <div className="space-y-4">
          {/* Primary metrics */}
          <div className="grid grid-cols-4 gap-3">
            <MetricCard
              label="TRAINING SAMPLES"
              value={results.training_samples}
              highlight
            />
            <MetricCard
              label="PHYSICAL AUC"
              value={results.validation_metrics.physical_auc?.toFixed(3) || '--'}
              subValue={`ACC: ${((results.validation_metrics.physical_accuracy || 0) * 100).toFixed(1)}%`}
            />
            <MetricCard
              label="REGULATORY AUC"
              value={results.validation_metrics.regulatory_auc?.toFixed(3) || '--'}
              subValue={`ACC: ${((results.validation_metrics.regulatory_accuracy || 0) * 100).toFixed(1)}%`}
            />
            <MetricCard
              label="META ACCURACY"
              value={`${((results.validation_metrics.meta_accuracy || 0) * 100).toFixed(1)}%`}
              highlight
            />
          </div>

          {/* Secondary metrics */}
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="FINANCIAL RMSE"
              value={results.validation_metrics.financial_rmse?.toFixed(0) || '--'}
              subValue="COST PREDICTION ERROR ($)"
            />
            <MetricCard
              label="TIMELINE RMSE"
              value={results.validation_metrics.timeline_rmse?.toFixed(2) || '--'}
              subValue="YEARS PREDICTION ERROR"
            />
          </div>

          {/* Data distribution */}
          {results.data_summary && (
            <div className="border border-mono-800">
              <div className="border-b border-mono-800 px-4 py-3">
                <h4 className="text-xs text-mono-300 tracking-widest">DATA DISTRIBUTION</h4>
              </div>
              <div className="p-4 grid grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] text-mono-500 tracking-widest mb-2">PHYSICAL RISK</p>
                  <div className="flex gap-2">
                    {Object.entries(results.data_summary.physical_risk_distribution).map(([key, val]) => (
                      <div key={key} className="text-center">
                        <p className="text-lg font-bold text-mono-200">{val}</p>
                        <p className="text-[10px] text-mono-500">{key === '0' ? 'LOW' : 'HIGH'}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-mono-500 tracking-widest mb-2">REGULATORY RISK</p>
                  <div className="flex gap-2">
                    {Object.entries(results.data_summary.regulatory_risk_distribution).map(([key, val]) => (
                      <div key={key} className="text-center">
                        <p className="text-lg font-bold text-mono-200">{val}</p>
                        <p className="text-[10px] text-mono-500">{key === '0' ? 'LOW' : 'HIGH'}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-mono-500 tracking-widest mb-2">THREAT TIERS</p>
                  <div className="flex gap-2">
                    {Object.entries(results.data_summary.threat_tier_distribution)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([key, val]) => (
                      <div key={key} className="text-center">
                        <p className="text-lg font-bold text-mono-200">{val}</p>
                        <p className="text-[10px] text-mono-500">T{key}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'features' && results.feature_importance && (
        <div className="grid grid-cols-2 gap-4">
          <FeatureImportanceChart
            data={results.feature_importance.physical_risk}
            title="PHYSICAL RISK MODEL"
          />
          <FeatureImportanceChart
            data={results.feature_importance.regulatory_risk}
            title="REGULATORY RISK MODEL"
          />
          <FeatureImportanceChart
            data={results.feature_importance.financial_risk}
            title="FINANCIAL RISK MODEL"
          />
          <FeatureImportanceChart
            data={results.feature_importance.timeline_risk}
            title="TIMELINE RISK MODEL"
          />
        </div>
      )}

      {activeTab === 'confusion' && results.confusion_matrices && (
        <div className="grid grid-cols-3 gap-4">
          {results.confusion_matrices.physical_risk && (
            <ConfusionMatrix
              data={results.confusion_matrices.physical_risk}
              title="PHYSICAL RISK"
            />
          )}
          {results.confusion_matrices.regulatory_risk && (
            <ConfusionMatrix
              data={results.confusion_matrices.regulatory_risk}
              title="REGULATORY RISK"
            />
          )}
          {results.confusion_matrices.threat_tier && (
            <ConfusionMatrix
              data={results.confusion_matrices.threat_tier}
              title="THREAT TIER"
            />
          )}
        </div>
      )}
    </div>
  );
}

// Main component
export function ModelTraining() {
  const { data: modelInfo, isLoading: modelLoading } = useModelInfo();
  const { data: versionsData } = useModelVersions();
  const trainMutation = useTrainModel();
  const [trainingResults, setTrainingResults] = useState<TrainingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTrain = async () => {
    setError(null);
    setTrainingResults(null);

    try {
      const results = await trainMutation.mutateAsync({
        min_sites: 5,
        validation_split: 0.2,
      });
      setTrainingResults(results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Training failed');
    }
  };

  return (
    <div className="border border-mono-800">
      <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
        <h3 className="text-xs text-mono-300 tracking-widest">MODEL TRAINING</h3>
        {modelInfo?.client_model?.has_trained_model && (
          <span className="text-[10px] text-mono-500">
            ACTIVE: {modelInfo.client_model.model_version}
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Current model status */}
        <div className="grid grid-cols-4 gap-3">
          <div className="border border-mono-700 p-3">
            <p className="text-[10px] text-mono-500 tracking-widest">STATUS</p>
            <p className="text-sm text-mono-200 mt-1">
              {modelInfo?.client_model?.has_trained_model ? 'TRAINED' : 'NOT TRAINED'}
            </p>
          </div>
          <div className="border border-mono-700 p-3">
            <p className="text-[10px] text-mono-500 tracking-widest">VERSION</p>
            <p className="text-sm text-mono-200 mt-1 font-mono">
              {modelInfo?.client_model?.model_version || '--'}
            </p>
          </div>
          <div className="border border-mono-700 p-3">
            <p className="text-[10px] text-mono-500 tracking-widest">SAMPLES</p>
            <p className="text-sm text-mono-200 mt-1">
              {modelInfo?.client_model?.training_samples || 0}
            </p>
          </div>
          <div className="border border-mono-700 p-3">
            <p className="text-[10px] text-mono-500 tracking-widest">FEATURES</p>
            <p className="text-sm text-mono-200 mt-1">
              {modelInfo?.feature_count || 0}
            </p>
          </div>
        </div>

        {/* Training button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleTrain}
            disabled={trainMutation.isPending}
            className="px-6 py-3 border border-mono-100 bg-mono-100 text-mono-950 text-xs tracking-widest hover:bg-mono-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {trainMutation.isPending ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                TRAINING...
              </span>
            ) : (
              'TRAIN MODEL'
            )}
          </button>

          {trainMutation.isPending && (
            <p className="text-[10px] text-mono-500 tracking-wider">
              EXTRACTING FEATURES AND TRAINING XGBOOST ENSEMBLE...
            </p>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="border border-mono-500 bg-mono-900 p-4">
            <p className="text-xs text-mono-300 tracking-wider">{error}</p>
          </div>
        )}

        {/* Training results */}
        {trainingResults && <TrainingResults results={trainingResults} />}

        {/* Previous versions */}
        {versionsData && versionsData.versions.length > 0 && !trainingResults && (
          <div className="border border-mono-800">
            <div className="border-b border-mono-800 px-4 py-3">
              <h4 className="text-xs text-mono-300 tracking-widest">MODEL HISTORY</h4>
            </div>
            <div className="divide-y divide-mono-800">
              {versionsData.versions.slice(0, 5).map((version) => (
                <div key={version.version} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-mono-200 font-mono">{version.version}</p>
                    <p className="text-[10px] text-mono-500">
                      {new Date(version.training_date).toLocaleDateString()} | {version.training_samples} samples
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {version.validation_auc && (
                      <span className="text-xs text-mono-400">
                        AUC: {version.validation_auc.toFixed(3)}
                      </span>
                    )}
                    {version.is_active && (
                      <span className="px-2 py-1 border border-mono-500 text-[10px] text-mono-300 tracking-widest">
                        ACTIVE
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
